

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSubscriptionPlans } from '../api/dbApi';
import Layout from '../layout/Layout';
import PageTransition from '../components/PageTransition';
import { getStoredUser } from '../utilities/encryption';
import Swal from 'sweetalert2';

export default function Pricing() {
  const navigate = useNavigate();
  const user = getStoredUser();
  const userId = user?.data?.id;

  const [plans, setPlans] = useState([]);
  const [activeSub, setActiveSub] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getSubscriptionPlans(userId);
      setPlans(res.data?.plans || []);
      setActiveSub(res.data?.current_subscription || null);
    } catch (error) {
      console.error("Error loading plans", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  return (
    <Layout>
      <PageTransition>
        <div className="container py-5">
          {/* STRATEGIC HEADER */}
          <div className="text-center mb-5">
            <span className="badge bg-indigo-subtle text-indigo rounded-pill px-3 py-2 mb-3 fw-bold tracking-wider">PRICING PLANS</span>
            <h1 className="fw-black display-5 mb-3">Scale your outreach without <span className="color-theme">Limits</span></h1>
            <p className="text-muted mx-auto fw-medium" style={{ maxWidth: '580px' }}>
              Simple, transparent pricing. Whether you're a startup or an enterprise, we have a plan designed for your growth.
            </p>
          </div>

          

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary border-2" role="status"></div>
            </div>
          ) : (
            <div className="row justify-content-center align-items-stretch g-4">
              {plans.map((plan, index) => {
                const isCurrent = activeSub?.plan_id === plan.id;
                // Highlight the middle plan (index 1) or one marked as 'Pro'
                const isFeatured = plan.name.toLowerCase().includes('pro') || index === 1;

                return (
                  <div className="col-lg-4 col-md-6" key={plan.id}>
                    <div className={`card h-100 border-0 rounded-4 transition-hover position-relative ${isFeatured ? 'shadow-lg border-primary-fade' : 'shadow-sm border'}`}>
                      
                      {isFeatured && (
                        <div className="position-absolute top-0 start-50 translate-middle">
                          <span className="badge rounded-pill bg-primary px-3 py-2 shadow-sm border border-white border-2 fw-bold">MOST POPULAR</span>
                        </div>
                      )}

                      <div className="card-body p-4 p-xl-5 d-flex flex-column">
                        <div className="mb-4">
                          <h4 className="fw-bold mb-1 text-dark">{plan.name}</h4>
                          <p className="text-muted small fw-medium">Essential tools for professional scaling.</p>
                        </div>

                        <div className="d-flex align-items-baseline mb-4">
                          <span className="h4 fw-bold mb-0">₹</span>
                          <span className="display-4 fw-black mb-0 mx-1">{plan.price}</span>
                          <span className="text-muted fw-bold small text-uppercase tracking-tighter">/{plan.duration_days} days</span>
                        </div>

                        <ul className="list-unstyled mb-auto">
                          <li className="mb-4 d-flex align-items-start">
                            <div className="bg-success-subtle rounded-circle p-1 me-3">
                              <i className="bi bi-check2 text-success fw-bold"></i>
                            </div>
                            <div>
                              <span className="fw-bold d-block text-dark small">Device Nodes</span>
                              <small className="text-muted">Connect up to {plan.device_limit} active WhatsApp devices</small>
                            </div>
                          </li>
                          <li className="mb-4 d-flex align-items-start">
                            <div className="bg-success-subtle rounded-circle p-1 me-3">
                              <i className="bi bi-check2 text-success fw-bold"></i>
                            </div>
                            <div>
                              <span className="fw-bold d-block text-dark small">Broadcast Limit</span>
                              <small className="text-muted">{plan.message_limit.toLocaleString()} cloud messages per cycle</small>
                            </div>
                          </li>
                          <li className="mb-4 d-flex align-items-start">
                            <div className="bg-success-subtle rounded-circle p-1 me-3">
                              <i className="bi bi-check2 text-success fw-bold"></i>
                            </div>
                            <div>
                              <span className="fw-bold d-block text-dark small">Webhook Integration</span>
                              <small className="text-muted">Full access to real-time message events</small>
                            </div>
                          </li>
                        </ul>

                        <div className="mt-5">
                          <button 
                            className={`btn w-100 py-3 rounded-3 fw-bold border-0 transition-all ${
                              isCurrent 
                              ? 'btn-success disabled opacity-75' 
                              : isFeatured 
                                ? 'btn-primary shadow-primary custom-gradient-bg shadow-sm' 
                                : 'btn-outline-dark border-2'
                            }`}
                            onClick={() => !isCurrent && navigate('/checkout', { state: { plan } })}
                          >
                            {isCurrent ? (
                              <span><i className="bi bi-shield-check me-2"></i> Current Active Plan</span>
                            ) : (
                              'Upgrade Account'
                            )}
                          </button>
                          <p className="text-center text-muted x-small mt-3 mb-0 fw-medium">
                            <i className="bi bi-lightning-charge-fill text-warning me-1"></i> Instant Activation upon payment
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* TRUST FOOTER */}
          <div className="row mt-5 pt-5 justify-content-center border-top">
            <div className="col-md-10 d-flex flex-column flex-md-row justify-content-between align-items-center gap-4 text-center text-md-start">
              <div className="d-flex align-items-center gap-3">
                <i className="bi bi-patch-check-fill text-primary fs-3"></i>
                <div>
                  <h6 className="mb-0 fw-bold">Money Back Guarantee</h6>
                  <p className="text-muted small mb-0">Unsatisfied? We offer a 7-day refund policy.</p>
                </div>
              </div>
              <div className="d-flex align-items-center gap-3">
                <i className="bi bi-shield-lock-fill text-success fs-3"></i>
                <div>
                  <h6 className="mb-0 fw-bold">Secure Transactions</h6>
                  <p className="text-muted small mb-0">PCI-DSS compliant 256-bit encrypted payments.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageTransition>
    </Layout>
  );
}